import React, { useEffect, useRef, useState } from 'react';
import { Icon, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

const SCROLL_STEP = 200;

const VersionTabsComponent = ({
  versions = [],
  activeVersion,
  counts = {},
  inactiveVersions,
  unmatchedVersions,
  requiredVersionName = 'V1',
  onSelectVersion,
  onAddVersion,
  onRemoveVersion,
  // onRenameVersion, // renaming disabled; kept for a future iteration
}) => {
  const { t } = useTranslation();
  const isInactive = (version) => !!(inactiveVersions && inactiveVersions.has(version));
  const isUnmatched = (version) => !!(unmatchedVersions && unmatchedVersions.has(version));

  const scrollRef = useRef(null);
  const activeTabRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [versions]);

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersion]);

  const scrollBy = (delta) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
      {canScrollLeft && (
        <Icon
          type="left"
          style={{ fontSize: 14, color: '#595959', cursor: 'pointer', padding: 4, flexShrink: 0 }}
          onClick={() => scrollBy(-SCROLL_STEP)}
        />
      )}
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="version-tabs"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <style>{'.version-tabs::-webkit-scrollbar { display: none; }'}</style>
        {versions.map((version) => {
          const isActive = version === activeVersion;
          const versionInactive = isInactive(version);
          const versionUnmatched = isUnmatched(version);
          return (
            <div
              key={version}
              ref={isActive ? activeTabRef : null}
              onClick={() => onSelectVersion(version)}
              role="button"
              tabIndex={0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderBottom: isActive ? '2px solid #1890ff' : '2px solid transparent',
                cursor: 'pointer',
                opacity: versionInactive ? 0.6 : 1,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ color: isActive ? '#1890ff' : 'inherit', fontWeight: isActive ? 600 : 400 }}>
                {version}
              </span>
              {version === requiredVersionName && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#1890ff',
                    background: '#e6f4ff',
                    borderRadius: 10,
                    padding: '2px 8px',
                  }}
                >
                  {t('REQUIRED')}
                </span>
              )}
              {versionInactive && (
                <Tooltip title={t('This version is inactive; its remaining outputs were redistributed.')}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#8c8c8c',
                      background: '#f0f0f0',
                      borderRadius: 10,
                      padding: '2px 8px',
                    }}
                  >
                    {t('Inactive version')}
                  </span>
                </Tooltip>
              )}
              {versionUnmatched && (
                <Tooltip title={t('This version has ads that do not match any screen; they will not be saved.')}>
                  <Icon type="warning" theme="filled" style={{ color: '#f5222d', fontSize: 14 }} />
                </Tooltip>
              )}
              <span
                style={{
                  fontSize: 12,
                  color: '#595959',
                  background: '#f0f0f0',
                  borderRadius: 10,
                  padding: '1px 8px',
                }}
              >
                {counts[version] || 0}
              </span>
              {/* Rename disabled; kept for a future iteration.
              {isActive && !versionInactive && (
                <Tooltip title={t('Rename version')}>
                  <Icon
                    type="edit"
                    style={{ color: '#1890ff', fontSize: 12 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRenameVersion(version);
                    }}
                  />
                </Tooltip>
              )}
              */}
              {isActive && !versionInactive && version !== requiredVersionName && (
                <Tooltip title={t('Delete version')}>
                  <Icon
                    type="delete"
                    style={{ color: '#1890ff', fontSize: 12 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveVersion(version);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        })}
        <Tooltip title={t('Add version')}>
          <Icon
            type="plus-circle"
            style={{ fontSize: 18, cursor: 'pointer', marginLeft: 8, flexShrink: 0 }}
            onClick={onAddVersion}
          />
        </Tooltip>
      </div>
      {canScrollRight && (
        <Icon
          type="right"
          style={{ fontSize: 14, color: '#595959', cursor: 'pointer', padding: 4, flexShrink: 0 }}
          onClick={() => scrollBy(SCROLL_STEP)}
        />
      )}
    </div>
  );
};

export default VersionTabsComponent;
